
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

interface ImageEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (imageUrl: string) => void;
  originalImageUrl: string;
  defaultImageUrl?: string;
  title?: string;
  description?: string;
}

const ImageEditor = ({
  isOpen,
  onClose,
  onSave,
  originalImageUrl,
  defaultImageUrl,
  title = "Upload Image",
  description
}: ImageEditorProps) => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use effect to sync the isOpen prop with internal state
  useEffect(() => {
    if (isOpen && !internalIsOpen) {
      setInternalIsOpen(true);
      setPreviewUrl(originalImageUrl || '');
      setSelectedImage(null);
    }
  }, [isOpen, originalImageUrl, internalIsOpen]);

  const handleClose = () => {
    setInternalIsOpen(false);
    onClose();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Add file size validation
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size exceeds 5MB. Please choose a smaller image.");
        return;
      }
      
      setSelectedImage(file);
      
      // Create a more persistent data URL instead of a temporary blob URL
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          setPreviewUrl(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (selectedImage && previewUrl) {
      // Call the onSave function with the preview URL
      onSave(previewUrl);
      handleClose();
    } else if (previewUrl) {
      // If there's already a preview URL but no new file selected
      onSave(previewUrl);
      handleClose();
    } else {
      toast.error("Please select an image first");
    }
  };

  const handleResetToDefault = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (defaultImageUrl) {
      setPreviewUrl(defaultImageUrl);
      setSelectedImage(null);
      onSave(defaultImageUrl);
      handleClose();
    }
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Dialog 
      open={internalIsOpen} 
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-md" onClick={stopPropagation}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="image-upload">Select Image</Label>
            <Input 
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
          </div>
          
          {/* Add image size recommendation */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="flex items-start">
              <span className="text-blue-500 mr-2">ℹ️</span>
              <div className="text-sm text-blue-700">
                <p><strong>Recommended image dimensions:</strong></p>
                <ul className="list-disc ml-4 mt-1">
                  <li>Banner images: 1200×400 pixels (3:1 aspect ratio)</li>
                  <li>Topic images: 800×600 pixels (4:3 aspect ratio)</li>
                  <li>Maximum file size: 5MB</li>
                </ul>
              </div>
            </div>
          </div>
          
          {previewUrl && (
            <div className="mt-4">
              <Label>Preview</Label>
              <div className="mt-2 border rounded-md overflow-hidden">
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="w-full h-40 object-cover"
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex justify-between sm:justify-between">
          {defaultImageUrl && (
            <div>
              <Button 
                variant="outline" 
                onClick={handleResetToDefault}
                className="mr-2"
                type="button"
              >
                Reset to Default
              </Button>
            </div>
          )}
          <div className={`flex gap-2 ${!defaultImageUrl ? 'w-full justify-end' : ''}`}>
            <Button variant="outline" onClick={handleClose} type="button">
              Cancel
            </Button>
            <Button onClick={handleImageUpload} className="bg-brand-blue hover:bg-brand-darkBlue" type="button">
              Save Image
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageEditor;
